import { createAsyncThunk } from '@reduxjs/toolkit'
import { get, remove } from '../services/crud'
import { ServiceConfig } from '../services/config'

import {
  ApiError,
  ApiErrorWithId,
  IAttachmentDTO,
  ICreateAttachmentInput,
  ICreateAttachmentResponse,
  IGetAttachmentsRequest,
  IUpdateAttachmentProgressModel,
} from './typings'
import { getAuthHeader } from '@user/store/services/crud'
import { reset, setProgress, setStatus } from './slice'

const baseUrl = `${ServiceConfig.baseAPIUrl}/Attachments`
const UPLOAD_ERROR_FALLBACK = 'Unable to upload attachment'

// The API answers validation problems (HTTP 400) with { Message }; show that
// text to the user. Other failures keep the generic message.
const getUploadErrorMessage = (xhr: XMLHttpRequest): string => {
  if (xhr.status === 400) {
    try {
      const body = JSON.parse(xhr.responseText)
      if (typeof body?.Message === 'string' && body.Message) return body.Message
    } catch {
      // response was not JSON; fall through to the generic message
    }
  }
  return xhr.statusText || UPLOAD_ERROR_FALLBACK
}

// ========== U P L O A D   A T T A C H M E N T S ==========
export const addAttachments = createAsyncThunk<
  ICreateAttachmentResponse,
  ICreateAttachmentInput,
  { rejectValue: ApiErrorWithId }
>('attachments/add', async (model, thunkApi) => {
  const authHeader = getAuthHeader() || {}
  const formData = new FormData()
  formData.append('json', JSON.stringify(model))
  formData.append('file', model.streamContent)

  return new Promise<ICreateAttachmentResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${baseUrl}`, true)

    Object.keys(authHeader).forEach((key) => {
      xhr.setRequestHeader(key, authHeader[key])
    })

    xhr.withCredentials = true

    xhr.upload.onprogress = (progressEvent) => {
      if (progressEvent.lengthComputable) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        )
        const progressModel: IUpdateAttachmentProgressModel = {
          id: model.id,
          progress: percentCompleted,
        }
        thunkApi.dispatch(setProgress(progressModel))
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.responseText)
        resolve({ ...response, tempId: model.id } as ICreateAttachmentResponse)
      } else {
        reject(
          thunkApi.rejectWithValue({
            id: model.id,
            Message: getUploadErrorMessage(xhr),
          }),
        )
      }
    }

    xhr.onerror = () => {
      reject(
        thunkApi.rejectWithValue({
          id: model.id,
          Message: xhr.statusText || UPLOAD_ERROR_FALLBACK,
        }),
      )
    }

    xhr.send(formData)
  })
})

// ========== D E L E T E   A T T A C H M E N T ==========
export const deleteAttachment = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorWithId }
>('attachments/delete', async (id, thunkApi) => {
  const response = await remove(`${baseUrl}?attachmentId=${id}`)
  thunkApi.dispatch(setStatus({ id, status: 'idle' }))
  if (response.ok) return id
  const error = await response.json()
  return thunkApi.rejectWithValue({ id: id, Message: error })
})

// ========== G E T   A L L   A T T A C H M E N T S   F O R   L E A D   /   O P P O R T U N I T Y ==========
export const getAttachments = createAsyncThunk<
  IAttachmentDTO[],
  IGetAttachmentsRequest,
  { rejectValue: ApiError }
>('attachments/get', async (model, thunkApi) => {
  thunkApi.dispatch(reset())
  const response = await get(
    `${baseUrl}/all?isLeadAttached=${model.isLeadAttached}&assignToEntity=${model.assignToEntity}`,
  )

  if (response.ok) {
    const payload: IAttachmentDTO[] = await response.json()
    payload.map((a) => (a.status = 'idle'))

    if (payload) {
      payload.forEach((attachment) => {
        attachment.createdDate = new Date(attachment.createdDate)
      })
    }

    return payload
  }
  return thunkApi.rejectWithValue((await response.json()) as ApiError)
})
